import { useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation, Trans } from 'react-i18next'
import { pageUrl } from '../seo'
import { Plus, Minus } from 'lucide-react'

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
}

const PAGE_BG = {
    backgroundImage: `url(/assets/images/rapariga-scaled.png)`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
}

const LINK_STYLE = { color: '#00203F', fontWeight: 700, textDecoration: 'underline' }

function Accordion({ id, title, children, open, onToggle }) {
    return (
        <div className="overflow-hidden rounded-xl border" style={{ borderColor: 'rgba(0,32,63,0.15)', backgroundColor: '#ffffff' }}>
            {/* O título é um heading (navegação por cabeçalhos nos leitores de ecrã);
                o botão vive DENTRO do heading — heading dentro de button é HTML inválido. */}
            <h2>
                <button
                    onClick={onToggle}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left font-bold transition-colors hover:opacity-90 focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-inset"
                    style={{ color: '#00203F', fontFamily: 'Rubik, system-ui, sans-serif' }}
                    aria-expanded={open}
                    /* o painel só existe no DOM quando aberto — um aria-controls a
                       apontar para um id inexistente é um IDREF inválido */
                    aria-controls={open ? id : undefined}
                >
                    <span className="text-base">{title}</span>
                    <span className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: '#00203F', color: '#ADEFD1' }}>
                        {open ? <Minus size={14} /> : <Plus size={14} />}
                    </span>
                </button>
            </h2>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        key="content"
                        id={id}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                    >
                        <div className="px-6 pb-6 pt-2 leading-relaxed space-y-3 text-base" style={{ color: '#00203F' }}>
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default function OQueFazer() {
    const { t } = useTranslation()
    const [activeTab, setActiveTab] = useState(0)
    /* Primeiro acordeão aberto por defeito: o conteúdo é a substância da página. */
    const [openTab1, setOpenTab1] = useState(0)
    const tabRefs = useRef([])

    const toggleTab1 = (i) => setOpenTab1(openTab1 === i ? null : i)

    const tabs = t('oQueFazer.tabs', { returnObjects: true })

    /* Num tablist só o separador ativo é alcançável com Tab; a troca faz-se com as
       setas (padrão ARIA). Sem isto, o segundo separador ficava fora do teclado.
       Tablist horizontal: só esquerda/direita — capturar cima/baixo tirava ao
       teclado a forma de fazer scroll à página com o foco nos separadores. */
    const handleTabKeys = (e) => {
        const ultimo = tabs.length - 1
        let alvo = null
        if (e.key === 'ArrowRight') alvo = activeTab === ultimo ? 0 : activeTab + 1
        else if (e.key === 'ArrowLeft') alvo = activeTab === 0 ? ultimo : activeTab - 1
        else if (e.key === 'Home') alvo = 0
        else if (e.key === 'End') alvo = ultimo
        if (alvo === null) return
        e.preventDefault()
        setActiveTab(alvo)
        tabRefs.current[alvo]?.focus()
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen"
            style={PAGE_BG}
        >
            <title>{t('oQueFazer.metaTitle')}</title>
            <meta name="description" content={t('oQueFazer.metaDescription')} />
            {/* pageUrl() já inclui a base da língua (/, /en/, /uk/, …) */}
            <link rel="canonical" href={pageUrl('o-que-fazer')} />
            <meta property="og:url" content={pageUrl('o-que-fazer')} />
            {/* Hero */}
            <section className="py-16 lg:py-24" style={{ backgroundColor: '#00203F' }}>
                <div className="container-custom">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="font-heading text-4xl lg:text-5xl font-bold text-white leading-tight"
                    >
                        {t('oQueFazer.h1')}
                    </motion.h1>
                    <div className="mt-6 h-1 w-24 rounded-full" style={{ backgroundColor: '#ADEFD1' }} />
                </div>
            </section>

            {/* Content — opaque white over bg */}
            <section className="section-padding" style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}>
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl mx-auto"
                    >
                        {/* Tab Buttons */}
                        <div className="flex flex-wrap gap-2 mb-10" role="tablist" aria-label={t('oQueFazer.tablistAria')}>
                            {tabs.map((tab, i) => (
                                <button
                                    key={i}
                                    ref={(el) => { tabRefs.current[i] = el }}
                                    onClick={() => setActiveTab(i)}
                                    onKeyDown={handleTabKeys}
                                    role="tab"
                                    id={`tab-${i}`}
                                    aria-selected={activeTab === i}
                                    /* só o painel ativo está montado; apontar para o
                                       painel inativo seria um IDREF inválido */
                                    aria-controls={activeTab === i ? `painel-${i}` : undefined}
                                    tabIndex={activeTab === i ? 0 : -1}
                                    className="px-5 py-3 font-bold text-sm rounded-lg transition-all focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2"
                                    style={
                                        activeTab === i
                                            ? { backgroundColor: '#ADEFD1', color: '#00203F', fontFamily: 'Rubik, system-ui, sans-serif' }
                                            : { backgroundColor: '#00203F', color: '#ffffff', fontFamily: 'Rubik, system-ui, sans-serif' }
                                    }
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        <AnimatePresence mode="wait">
                            {/* Tab 0 — Se for vítima */}
                            {activeTab === 0 && (
                                <motion.div
                                    key="tab0"
                                    id="painel-0"
                                    role="tabpanel"
                                    aria-labelledby="tab-0"
                                    tabIndex={0}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.25 }}
                                    className="space-y-4 leading-relaxed text-base focus:outline-hidden"
                                    style={{ color: '#00203F' }}
                                >
                                    <p>{t('oQueFazer.vitima.p1')}</p>
                                    <p>
                                        <Trans
                                            i18nKey="oQueFazer.vitima.p2"
                                            components={{ a: <a href={pageUrl('direitos').replace('https://traficopessoas.apav.pt', '')} style={LINK_STYLE} className="hover:underline" /> }}
                                        />
                                    </p>
                                    <ul className="space-y-4 mt-2">
                                        <li className="flex gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(0,32,63,0.04)', border: '1px solid rgba(0,32,63,0.08)' }}>
                                            <span className="font-bold shrink-0 mt-0.5" style={{ color: '#00203F' }}>•</span>
                                            <div>
                                                {t('oQueFazer.vitima.item1')}{' '}
                                                <ul className="mt-2 ml-4 space-y-1">
                                                    <li>
                                                        {t('oQueFazer.vitima.item1Label')} —{' '}
                                                        <a href="https://www.policiajudiciaria.pt/onde-estamos/" target="_blank" rel="noopener noreferrer" style={LINK_STYLE} className="hover:underline break-all">
                                                            https://www.policiajudiciaria.pt/onde-estamos/
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </li>
                                        <li className="flex gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(0,32,63,0.04)', border: '1px solid rgba(0,32,63,0.08)' }}>
                                            <span className="font-bold shrink-0 mt-0.5" style={{ color: '#00203F' }}>•</span>
                                            <div>
                                                {t('oQueFazer.vitima.item2')}{' '}
                                                <ul className="mt-2 ml-4 space-y-1">
                                                    <li>
                                                        {t('oQueFazer.vitima.item2Label')} —{' '}
                                                        <a href="https://www.otsh.mai.gov.pt/servicos/" target="_blank" rel="noopener noreferrer" style={LINK_STYLE} className="hover:underline break-all">
                                                            https://www.otsh.mai.gov.pt/servicos/
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </li>
                                        <li className="flex gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(0,32,63,0.04)', border: '1px solid rgba(0,32,63,0.08)' }}>
                                            <span className="font-bold shrink-0 mt-0.5" style={{ color: '#00203F' }}>•</span>
                                            <span>
                                                <Trans
                                                    i18nKey="oQueFazer.vitima.item3"
                                                    components={{ a: <a href="tel:112" style={LINK_STYLE} className="hover:underline" /> }}
                                                />
                                            </span>
                                        </li>
                                    </ul>
                                </motion.div>
                            )}

                            {/* Tab 1 — Se identificar vítimas */}
                            {activeTab === 1 && (
                                <motion.div
                                    key="tab1"
                                    id="painel-1"
                                    role="tabpanel"
                                    aria-labelledby="tab-1"
                                    tabIndex={0}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.25 }}
                                    className="space-y-4 text-base focus:outline-hidden"
                                    style={{ color: '#00203F' }}
                                >
                                    <p className="leading-relaxed">{t('oQueFazer.identificar.p1')}</p>
                                    <p className="leading-relaxed">{t('oQueFazer.identificar.p2')}</p>
                                    <div className="mt-6 space-y-3">
                                        <Accordion
                                            title={t('oQueFazer.identificar.comoIdentificar.titulo')}
                                            id="como-identificar"
                                            open={openTab1 === 0}
                                            onToggle={() => toggleTab1(0)}
                                        >
                                            <p>{t('oQueFazer.identificar.comoIdentificar.intro')}</p>
                                            <ul className="space-y-2 mt-2">
                                                {t('oQueFazer.identificar.comoIdentificar.sinais', { returnObjects: true }).map((item) => (
                                                    <li key={item} className="flex gap-2">
                                                        <span className="shrink-0 mt-0.5" style={{ color: '#00203F' }}>•</span>
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </Accordion>

                                        <Accordion
                                            title={t('oQueFazer.identificar.comoAgir.titulo')}
                                            id="como-agir"
                                            open={openTab1 === 1}
                                            onToggle={() => toggleTab1(1)}
                                        >
                                            <p>{t('oQueFazer.identificar.comoAgir.p1')}</p>
                                            <p>{t('oQueFazer.identificar.comoAgir.p2')}</p>
                                            <p>{t('oQueFazer.identificar.comoAgir.p3')}</p>
                                            <p>{t('oQueFazer.identificar.comoAgir.p4')}</p>
                                            <ul className="space-y-2 mt-2">
                                                <li className="flex gap-2">
                                                    <span className="shrink-0 mt-0.5" style={{ color: '#00203F' }}>•</span>
                                                    <span>
                                                        {t('oQueFazer.identificar.comoAgir.item1')}{' '}
                                                        <a href="https://www.policiajudiciaria.pt/onde-estamos/" target="_blank" rel="noopener noreferrer" style={LINK_STYLE} className="hover:underline break-all">
                                                            https://www.policiajudiciaria.pt/onde-estamos/
                                                        </a>
                                                    </span>
                                                </li>
                                                <li className="flex gap-2">
                                                    <span className="shrink-0 mt-0.5" style={{ color: '#00203F' }}>•</span>
                                                    <span>
                                                        {t('oQueFazer.identificar.comoAgir.item2')}{' '}
                                                        <a href="https://www.otsh.mai.gov.pt/servicos/" target="_blank" rel="noopener noreferrer" style={LINK_STYLE} className="hover:underline break-all">
                                                            https://www.otsh.mai.gov.pt/servicos/
                                                        </a>
                                                    </span>
                                                </li>
                                            </ul>
                                        </Accordion>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                </div>
            </section>
        </motion.div>
    )
}
